import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import BilingualCallout from './BilingualCallout';
import ComparisonTable, { type TableColumn, type TableRow } from './ComparisonTable';
import {
  KeyTakeaways,
  StepCard,
  StepList,
  DocumentChecklist,
} from './Blocks';
import { H2, H3, P, Lead, UL, OL, LI, OLI, Strong } from './Prose';
import type { GuideBlock } from '@/content/guides/generated';

/**
 * Renders a generated guide's JSON blocks using the same editorial components
 * the hand-written guides use.
 *
 * WHY JSON AND NOT MDX/JSX: the pipeline writes model output straight into the
 * repo. Model-authored `.tsx` would be executable code entering the build — an
 * injection path that no amount of prompt instruction closes. JSON is inert
 * data, and this renderer is the only thing that turns it into markup, so the
 * blast radius of a bad generation is a malformed article, never arbitrary code.
 *
 * The same reasoning governs inline formatting: `**bold**` is the entire
 * supported syntax. No HTML is interpreted, so nothing the model emits can
 * introduce a tag, a link target, or a script.
 */

/** Parses the one supported inline marker. Everything else stays literal text. */
function renderInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
      <Strong key={i}>{part.slice(2, -2)}</Strong>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
}

function toTableProps(block: Extract<GuideBlock, { type: 'table' }>): {
  columns: TableColumn[];
  rows: TableRow[];
} {
  const columns: TableColumn[] = block.columns.map((c) => ({
    key: c.key,
    header: c.header,
    headerKo: c.headerKo,
    numeric: c.numeric,
  }));

  const rows: TableRow[] = block.rows.map((r) => ({
    label: r.label,
    labelKo: r.labelKo,
    note: r.note,
    highlight: r.highlight,
    highlightLabel: r.highlightLabel,
    // The model emits cells as an array of {key,value} because JSON Schema
    // can't express a dynamic-keyed object under additionalProperties:false.
    cells: Object.fromEntries(r.cells.map((c) => [c.key, c.value])),
  }));

  return { columns, rows };
}

function CommunityBanner({
  block,
  slug,
}: {
  block: Extract<GuideBlock, { type: 'communityCta' }>;
  slug: string;
}) {
  return (
    <aside className="my-10 overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card">
      <div className="h-1 w-full bg-jeok-500 dark:bg-jeok-dark" />
      <div className="p-5 sm:p-6">
        <h2 className="text-lg font-bold text-ink-900 dark:text-ink-50">{block.title}</h2>
        <p lang="ko" className="mt-0.5 font-korean text-sm text-ink-500 dark:text-ink-400">
          {block.titleKo}
        </p>

        <p className="mt-3 text-[15px] leading-7 text-ink-700 dark:text-ink-200">
          {renderInline(block.body)}
        </p>

        <blockquote className="mt-4 border-l-4 border-ink-200 pl-4 text-[15px] italic leading-7 text-ink-500 dark:border-surface-dark-border dark:text-ink-400">
          “{block.prompt}”
        </blockquote>

        <p className="mt-4 text-sm font-medium text-ink-700 dark:text-ink-200">
          Have a tip or a correction for this guide?
        </p>

        {/*
          Was "Join the Discussion" -> /community. The forum is retired, so this
          was both a redirect hop and the last user-generated-content entry
          point still rendered on public guide pages. It now goes to the contact
          form, which is a real, monitored destination.
        */}
        <Link
          to={`/contact?utm_source=guide&utm_content=${encodeURIComponent(slug)}`}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-jeok-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#A00D25] dark:bg-jeok-dark"
        >
          <MessageCircle className="h-4 w-4" aria-hidden="true" />
          Send us a note
        </Link>
      </div>
    </aside>
  );
}

export default function BlockRenderer({ blocks, slug }: { blocks: GuideBlock[]; slug: string }) {
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.type) {
          case 'lead':
            return <Lead key={i}>{renderInline(block.text)}</Lead>;

          case 'keyTakeaways':
            return <KeyTakeaways key={i} items={block.items.map(renderInline)} />;

          case 'heading':
            return block.level === 2 ? (
              <H2 key={i} id={block.id} ko={block.textKo}>
                {block.text}
              </H2>
            ) : (
              <H3 key={i} id={block.id}>
                {block.text}
              </H3>
            );

          case 'paragraph':
            return <P key={i}>{renderInline(block.text)}</P>;

          case 'list':
            return block.ordered ? (
              <OL key={i}>
                {block.items.map((item, j) => (
                  <OLI key={j}>{renderInline(item)}</OLI>
                ))}
              </OL>
            ) : (
              <UL key={i}>
                {block.items.map((item, j) => (
                  <LI key={j}>{renderInline(item)}</LI>
                ))}
              </UL>
            );

          case 'callout':
            return (
              <BilingualCallout
                key={i}
                variant={block.variant}
                title={block.title}
                titleKo={block.titleKo}
                childrenKo={
                  block.bodyKo?.length
                    ? block.bodyKo.map((p, j) => <p key={j}>{p}</p>)
                    : undefined
                }
              >
                {block.body.map((p, j) => (
                  <p key={j}>{renderInline(p)}</p>
                ))}
              </BilingualCallout>
            );

          case 'table': {
            const { columns, rows } = toTableProps(block);
            return (
              <ComparisonTable
                key={i}
                caption={block.caption}
                captionKo={block.captionKo}
                columns={columns}
                rows={rows}
                footnote={block.footnote}
              />
            );
          }

          case 'steps':
            return (
              <StepList key={i}>
                {block.items.map((step, j) => (
                  <StepCard
                    key={j}
                    step={j + 1}
                    title={step.title}
                    titleKo={step.titleKo}
                    meta={step.meta}
                  >
                    {step.body.map((p, k) => (
                      <p key={k}>{renderInline(p)}</p>
                    ))}
                  </StepCard>
                ))}
              </StepList>
            );

          case 'checklist':
            return (
              <DocumentChecklist
                key={i}
                storageKey={`koreanhub:checklist:${slug}`}
                title={block.title}
                titleKo={block.titleKo}
                items={block.items}
              />
            );

          case 'communityCta':
            return <CommunityBanner key={i} block={block} slug={slug} />;

          default:
            // A block type the renderer doesn't know is a pipeline bug, not
            // something to paper over — surface it in dev, drop it in prod.
            if (import.meta.env.DEV) {
              console.error('[BlockRenderer] unknown block', block);
            }
            return null;
        }
      })}
    </>
  );
}
